import React from 'react';
import { useInfiniteQuery } from '@tanstack/react-query';
import useUser from '../global/useUser';
import { searchItems } from '../../services/items';
import useDebouncedValue from '../useDebouncedValue';

interface Options {
  includeDeleted?: boolean;
  enabled?: boolean;
}

function useSearchItems(
  query: string,
  { includeDeleted = false, enabled = true }: Options = {},
) {
  const { user } = useUser();

  const debouncedQuery = useDebouncedValue(query, 500);
  const lowercasedQuery = debouncedQuery.trim().toLowerCase();

  const queryKey = React.useMemo(
    () => [
      'seach-retail-items',
      user?.business?.id,
      user?.role,
      includeDeleted,
      lowercasedQuery,
    ],
    [user?.business?.id, user?.role, includeDeleted, lowercasedQuery],
  );

  const {
    data,
    isLoading,
    isFetching,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey,
    queryFn: async ({ pageParam: cursor }) => {
      const result = await searchItems({
        businessId: user?.business?.id!,
        includeDeleted,
        query: lowercasedQuery,
        cursor,
      });

      return result;
    },
    enabled,
    initialPageParam: undefined as string | undefined,
    getNextPageParam: lastPage => {
      if (!lastPage.meta?.hasMore || !lastPage.meta?.cursor) return null;
      return lastPage.meta.cursor;
    },
  });

  const items = React.useMemo(
    () => data?.pages?.flatMap(page => page.result) ?? [],
    [data?.pages],
  );

  return {
    queryKey,
    data,
    items,
    isLoading,
    isFetching,
    isDebouncing: query !== debouncedQuery,
    isError,
    error,
    refetch,
    hasNextPage,
    fetchNextPage,
  };
}

export default useSearchItems;
